import React, { useState, useEffect, useRef } from 'react';

interface ThinkingDisplayProps {
  content: string;
  isStreaming: boolean;
}

/**
 * GPT-5のthinking（reasoning summary）を表示するコンポーネント
 *
 * AIが思考中の内容をリアルタイムで表示し、折りたたみ可能なUI
 */
const ThinkingDisplay: React.FC<ThinkingDisplayProps> = ({
  content,
  isStreaming
}) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const contentRef = useRef<HTMLDivElement>(null);

  // 新しいコンテンツが追加されたらスクロール
  useEffect(() => {
    if (isExpanded && contentRef.current && isStreaming) {
      contentRef.current.scrollTop = contentRef.current.scrollHeight;
    }
  }, [content, isExpanded, isStreaming]);

  if (!content) return null;

  return (
    <div className="mb-3 bg-gradient-to-r from-purple-50 to-indigo-50 dark:from-gray-800 dark:to-gray-750 rounded-lg border border-purple-200 dark:border-purple-700 overflow-hidden">
      {/* Header */}
      <div
        className="flex items-center justify-between px-3 py-2 cursor-pointer select-none hover:bg-purple-100/50 dark:hover:bg-gray-700/50 transition-colors"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center space-x-2">
          {isStreaming ? (
            <div className="flex items-center space-x-1">
              <span className="text-lg">💭</span>
              <span className="text-sm font-medium text-purple-700 dark:text-purple-300">
                思考中...
              </span>
              <div className="flex space-x-1 ml-1">
                <span className="w-1.5 h-1.5 bg-purple-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></span>
                <span className="w-1.5 h-1.5 bg-purple-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></span>
                <span className="w-1.5 h-1.5 bg-purple-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></span>
              </div>
            </div>
          ) : (
            <div className="flex items-center space-x-1">
              <span className="text-lg">🧠</span>
              <span className="text-sm font-medium text-purple-700 dark:text-purple-300">
                AIの思考プロセス
              </span>
            </div>
          )}
        </div>
        <span
          className={`text-purple-500 dark:text-purple-400 transition-transform duration-200 ${
            isExpanded ? 'rotate-180' : ''
          }`}
        >
          ▼
        </span>
      </div>

      {/* Content */}
      {isExpanded && (
        <div
          ref={contentRef}
          className="px-3 pb-3 max-h-48 overflow-y-auto"
        >
          <div className="text-sm text-gray-600 dark:text-gray-400 whitespace-pre-wrap leading-relaxed">
            {content}
          </div>
        </div>
      )}
    </div>
  );
};

export default ThinkingDisplay;
